import React, { createContext, useState, useContext, useCallback } from "react";
import {
  handleImageUpdateWithScroll,
  handleVideoUpdateWithScroll,
  sendScrollMessage,
  MESSAGE_TYPES,
} from "../utils/editUtils";

const MediaContext = createContext();

export function MediaProvider({ children }) {
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [targetField, setTargetField] = useState(null); // どのフィールド用に選択しているか
  const [fieldSetters, setFieldSetters] = useState({}); // フィールド名とセッター関数のマッピング
  const [currentMediaType, setCurrentMediaType] = useState(null); // 'image' または 'video'
  const [savedScrollPosition, setSavedScrollPosition] = useState(0); // スクロール位置を保存

  // 複数選択モード用の追加ステート
  const [selectionMode, setSelectionMode] = useState("single");
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  const [isMultipleFieldSetter, setIsMultipleFieldSetter] = useState({});

  // リサイズ横幅の状態管理
  const [savedResizeWidth, setSavedResizeWidth] = useState(null);

  // メディアを選択（複数選択対応）
  const selectMedia = useCallback(
    (media, forceAdd = false) => {
      console.log("🎯 selectMedia呼び出し開始", {
        media,
        selectionMode,
        forceAdd,
        currentSelectedMediaFiles: selectedMediaFiles,
        currentSelectedMediaFile: selectedMediaFile
      });

      if (selectionMode === "multiple") {
        console.log("📋 複数選択モード");
        // 既に選択されているかチェック
        const isAlreadySelected = selectedMediaFiles.some(
          (item) => item.id === media.id
        );
        console.log("🔍 既に選択されているか:", isAlreadySelected);

        if (isAlreadySelected && !forceAdd) {
          console.log("➖ 選択解除");
          // 通常クリック時：選択解除
          setSelectedMediaFiles((prev) =>
            prev.filter((item) => item.id !== media.id)
          );
        } else if (isAlreadySelected && forceAdd) {
          console.log("🔄 既に選択済み - 最後に移動（アップロード時）");
          // アップロード時：既に選択されている場合は、一旦除去してから最後に追加（最新表示のため）
          setSelectedMediaFiles((prev) => [
            ...prev.filter((item) => item.id !== media.id),
            media
          ]);
        } else {
          console.log("➕ 選択追加");
          // 選択追加
          setSelectedMediaFiles((prev) => [...prev, media]);
        }
      } else {
        console.log("📝 単一選択モード");
        // 単一選択モード：既に選択されている場合は選択解除、そうでなければ選択
        if (selectedMediaFile && selectedMediaFile.id === media.id) {
          console.log("➖ 選択解除");
          setSelectedMediaFile(null);
        } else {
          console.log("✅ selectedMediaFile更新:", media);
          setSelectedMediaFile(media);
        }
      }
      console.log("🏁 selectMedia終了");
    },
    [selectionMode, selectedMediaFiles, selectedMediaFile]
  );

  // リサイズ横幅を保存
  const saveResizeWidth = useCallback((width) => {
    setSavedResizeWidth(width);
  }, []);

  // リサイズ横幅を復元
  const restoreResizeWidth = useCallback(() => {
    return savedResizeWidth;
  }, [savedResizeWidth]);

  // メディアを閉じる
  const closeMedia = useCallback(() => {
    setIsMediaOpen(false);
    setTargetField(null);
    setSelectedMediaFiles([]);

    // 保存したスクロール位置に戻す
    setTimeout(() => {
      const editArea = document.querySelector(".editarea");
      if (editArea && savedScrollPosition !== undefined) {
        editArea.scrollTop = savedScrollPosition;
      }
    }, 0);
  }, [savedScrollPosition]);

  // セッター関数を登録（複数選択対応）
  const registerSetter = useCallback(
    (fieldName, setterFunction, isMultiple = false) => {
      console.log("📝 registerSetter:", fieldName);
      // fieldSettersとisMultipleFieldSetterを同時に更新
      setFieldSetters((prev) => ({
        ...prev,
        [fieldName]: setterFunction,
      }));

      setIsMultipleFieldSetter((prev) => ({
        ...prev,
        [fieldName]: isMultiple,
      }));
    },
    []
  );

  // メディアを開く（複数選択対応）
  const openMedia = useCallback(
    (fieldName, currentMedia = null, mediaType = null, mode = "single") => {
      setIsMediaOpen(true);
      setTargetField(fieldName);
      setCurrentMediaType(mediaType);
      setSelectionMode(mode);

      // 現在のメディアが渡された場合、それを選択状態にする
      if (mode === "multiple") {
        setSelectedMediaFiles(Array.isArray(currentMedia) ? currentMedia : []);
        setSelectedMediaFile(null);
      } else {
        setSelectedMediaFile(currentMedia);
        setSelectedMediaFiles([]);
      }

      // 現在のスクロール位置を保存
      const editArea = document.querySelector(".editarea");
      if (editArea) {
        setSavedScrollPosition(editArea.scrollTop);
        // MediaGallery表示のためにスクロール位置を一番上にリセット
        editArea.scrollTop = 0;
      }

      // ページに応じたcontentKeyを決定
      let pageContentKey = "topContent";
      // URLからページを判断
      const currentPath = window.location.pathname;
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent"; // instructorページを追加
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;
      if (
        fieldName === "hero_hedder_image" ||
        fieldName.includes("pc_video_") ||
        fieldName.includes("mobile_video_")
      ) {
        componentKey = currentPath.includes("about") ? "about" : "heroHeader";
      } else if (fieldName === "about_image") {
        componentKey = "story";
      } else if (fieldName === "newvisitor_bg_image") {
        componentKey = "newvisitor";
      } else if (fieldName.includes("concept_description_")) {
        componentKey = "about";
      } else if (fieldName.includes("main_image")) {
        componentKey = "studio";
      } else if (fieldName.includes("visual_images")) {
        componentKey = "studio";
      } else if (fieldName.includes("instructor_")) {
        componentKey = "instructor"; // instructorフィールドを追加
      }

      // iframeにスクロール命令を送信
      sendScrollMessage(fieldName, null, {
        contentKey: pageContentKey,
        componentKey,
      });
    },
    []
  );

  // メディアファイルを取得
  const fetchMediaFiles = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_media_files/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setMediaFiles(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  }, []);

  // 選択したメディアを確定（複数選択対応）
  const confirmMediaSelection = useCallback(() => {
    console.log("Confirming media selection:", {
      targetField,
      registeredKeys: Object.keys(fieldSetters),
      hasFieldSetter: !!(targetField && fieldSetters[targetField]),
      isMultiple: targetField && isMultipleFieldSetter[targetField],
      selectedMediaFile,
      selectedMediaFiles,
    });

    if (!targetField || !fieldSetters[targetField]) {
      return;
    }

    const isMultiple = isMultipleFieldSetter[targetField];

    if (isMultiple) {
      // 複数選択モードの場合（0枚選択も許可）
      fieldSetters[targetField](selectedMediaFiles);

      // ページに応じたcontentKeyを決定
      let pageContentKey = "topContent";
      const currentPath = window.location.pathname;
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent";
      } else if (currentPath.includes("newvisitor")) {
        pageContentKey = "newVisitorContent";
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;
      if (currentPath.includes("studio") && targetField === "visual_images") {
        componentKey = "studio";
      } else if (
        currentPath.includes("instructor") &&
        targetField.includes("instructor_")
      ) {
        componentKey = "instructor";
      }

      // iframeに通知
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        // iframe通信送信、ステート更新の完了を待つ
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
            target: `.edit_${targetField}`,
            contentKey: pageContentKey,
            componentKey,
            fieldName: targetField,
            value: selectedMediaFiles,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    } else {
      // 単一選択モードの場合（未選択も許可）
      console.log("📌 単一選択: setter呼び出し", targetField);
      try {
        fieldSetters[targetField](selectedMediaFile);
        console.log("📌 setter完了");
      } catch (e) {
        console.error("📌 setter呼び出しエラー:", e);
      }

      // ページに応じたcontentKeyを決定
      let pageContentKey = "topContent";
      // URLからページを判断
      const currentPath = window.location.pathname;
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent";
      } else if (currentPath.includes("newvisitor")) {
        pageContentKey = "newVisitorContent";
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;
      if (
        targetField === "hero_hedder_image" ||
        targetField.includes("pc_video_") ||
        targetField.includes("mobile_video_")
      ) {
        if (currentPath.includes("newvisitor")) {
          componentKey = "newvisitor";
        } else {
          componentKey = currentPath.includes("about") ? "about" : "heroHeader";
        }
      } else if (targetField === "about_image") {
        componentKey = "story";
      } else if (targetField === "newvisitor_bg_image") {
        componentKey = "newvisitor";
      } else if (targetField.includes("concept_description_")) {
        componentKey = "about";
      } else if (targetField.includes("main_image")) {
        componentKey = "studio";
      } else if (targetField.includes("instructor_")) {
        componentKey = "instructor";
      } else if (
        targetField.includes("empathy_") ||
        targetField.includes("appeal_") ||
        targetField.includes("conversion_") ||
        targetField.includes("reasons_")
      ) {
        componentKey = "newvisitor";
      }

      let isVideo = false;
      // 動画フィールドの場合
      if (targetField.includes("video")) {
        isVideo = true;
      }

      // 画像または動画の更新メッセージを送信（スクロール付き）
      console.log("📌 iframe通知送信:", { isVideo, pageContentKey, componentKey, targetField });
      try {
        if (isVideo) {
          handleVideoUpdateWithScroll(selectedMediaFile, targetField, {
            contentKey: pageContentKey,
            componentKey,
          });
        } else {
          handleImageUpdateWithScroll(selectedMediaFile, targetField, {
            contentKey: pageContentKey,
            componentKey,
          });
        }
        console.log("📌 iframe通知完了");
      } catch (e) {
        console.error("📌 iframe通知エラー:", e);
      }
    }

    closeMedia();
  }, [
    selectedMediaFile,
    selectedMediaFiles,
    targetField,
    fieldSetters,
    isMultipleFieldSetter,
    closeMedia,
  ]);

  // メディアを削除
  const handleDeleteMedia = useCallback(
    async (media) => {
      if (!media || !media.id) return;

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/delete_media/${media.id}/`,
          {
            method: "DELETE",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // レスポンスのJSONを解析
        const data = await response.json();

        if (!response.ok) {
          if (data.references) {
            // 参照情報を整形して表示（日本語表示名を使用）
            const referenceText = data.references
              .map(
                (ref) =>
                  `${ref.model_display}の${ref.field_display}で${ref.count}件使用中`
              )
              .join("\n");

            alert(
              `この画像は削除できません。\n以下の場所で使用されています：\n${referenceText}`
            );
          } else {
            alert(`エラー: ${data.error || "不明なエラーが発生しました"}`);
          }
          
          // 削除に失敗した場合でも選択状態を解除
          if (selectedMediaFile && selectedMediaFile.id === media.id) {
            setSelectedMediaFile(null);
          }
          
          // 複数選択モードの場合も選択から削除
          if (selectedMediaFiles.some((item) => item.id === media.id)) {
            setSelectedMediaFiles((prev) =>
              prev.filter((item) => item.id !== media.id)
            );
          }
          
          return;
        }

        // 削除成功したら、メディアリストを更新
        fetchMediaFiles();

        // 削除したメディアが選択中だった場合、選択を解除
        if (selectedMediaFile && selectedMediaFile.id === media.id) {
          setSelectedMediaFile(null);
        }

        // 複数選択モードの場合も選択から削除
        if (selectedMediaFiles.some((item) => item.id === media.id)) {
          setSelectedMediaFiles((prev) =>
            prev.filter((item) => item.id !== media.id)
          );
        }

        // 成功メッセージを表示（オプション）
        console.log("メディアが削除されました");
      } catch (error) {
        console.error("メディア削除中にエラーが発生しました:", error);
        alert(`削除中にエラーが発生しました: ${error.message}`);
      }
    },
    [selectedMediaFile, selectedMediaFiles, fetchMediaFiles]
  );

  // 複数メディアの一括削除
  const handleBulkDeleteMedia = useCallback(
    async (mediaList) => {
      if (!mediaList || mediaList.length === 0) return;

      const results = {
        success: [],
        failed: []
      };

      try {
        const apiUrl = process.env.REACT_APP_API_URL;

        // 各メディアの削除を並行実行
        const deletePromises = mediaList.map(async (media) => {
          try {
            const response = await fetch(
              `${apiUrl}/api/delete_media/${media.id}/`,
              {
                method: "DELETE",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const data = await response.json();

            if (response.ok) {
              results.success.push(media);
            } else {
              results.failed.push({
                media,
                error: data.error,
                references: data.references
              });
            }
          } catch (error) {
            results.failed.push({
              media,
              error: error.message,
              references: null
            });
          }
        });

        await Promise.all(deletePromises);

        // 結果をまとめて表示
        let message = "";
        
        if (results.success.length > 0) {
          message += `${results.success.length}件のメディアを削除しました。\n`;
        }

        if (results.failed.length > 0) {
          message += `\n以下の${results.failed.length}件は削除できませんでした：\n`;
          results.failed.forEach((item, index) => {
            message += `\n${index + 1}. メディアID: ${item.media.id}\n`;
            if (item.references && item.references.length > 0) {
              const referenceText = item.references
                .map(ref => `${ref.model_display}の${ref.field_display}で${ref.count}件使用中`)
                .join("\n");
              message += `${referenceText}\n`;
            } else {
              message += `エラー: ${item.error}\n`;
            }
          });
        }

        if (message) {
          alert(message);
        }

        // メディアリストを更新
        fetchMediaFiles();

        // 選択状態をクリア（成功・失敗問わず全てクリア）
        setSelectedMediaFiles([]);

      } catch (error) {
        console.error("一括削除中にエラーが発生しました:", error);
        alert(`一括削除中にエラーが発生しました: ${error.message}`);
      }
    },
    [fetchMediaFiles]
  );

  // ByteをMBに変換
  const bytesToMB = useCallback((bytesSize) => {
    if (bytesSize === null || bytesSize === undefined) {
      return null;
    }
    const mbSize = bytesSize / (1024 * 1024);
    return mbSize.toFixed(2); // 小数点以下2桁に丸める
  }, []);

  return (
    <MediaContext.Provider
      value={{
        isMediaOpen,
        mediaFiles,
        selectedMediaFile,
        selectedMediaFiles,
        selectionMode,
        targetField,
        currentMediaType,
        savedScrollPosition,
        openMedia,
        closeMedia,
        selectMedia,
        confirmMediaSelection,
        registerSetter,
        handleDeleteMedia,
        handleBulkDeleteMedia,
        bytesToMB,
        fetchMediaFiles,
        setMediaFiles,
        setSelectedMediaFiles,
        saveResizeWidth,
        restoreResizeWidth,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
}

export function useMedia() {
  return useContext(MediaContext);
}
